/**
 * number input field.
 * https://kpmg-au-ddx.atlassian.net/browse/KED-1696
 */
import {Component, Input, OnDestroy, OnInit, Output, EventEmitter, ElementRef, ViewChild, AfterViewInit} from "@angular/core";
import {UntypedFormControl, Validators} from "@angular/forms";
import {Answer} from "../../../../swaggerServices";
import {IAnswer, IFormField} from "../form-field-wrapper/form-field-wrapper.component";
import {Subscription} from "rxjs";
import { ConfigStateService } from "../../../app/config/config-state.service";

@Component({
    selector: "app-number-field",
    templateUrl: "./number-field.component.html",
    styleUrls: ["./number-field.component.scss"],
    standalone: false
})
export class NumberFieldComponent implements OnInit, AfterViewInit {
  @Input() myFormControl: UntypedFormControl;
  @Input() question: IFormField;
  @Input() disabled?: boolean;
  @Input() autofocus?: boolean = false;
  @Input() tabIndex?: number = 0;
  @Output() answer: Array<Answer>;
  @Output() blur: EventEmitter<any> = new EventEmitter();
  isFocused: boolean; // whether input is in focus
  numWithThousandsFormat: string
  decimalPlace: string = "0";
  
  @ViewChild('inputEl') inputEl: ElementRef;
  constructor(public config: ConfigStateService) {
  }

  ngOnInit() {
  }

  ngAfterViewInit() {
    if (this.autofocus) {
      this.inputEl.nativeElement.focus();
    }

    if (this.config.featureFlags.displayNumberUsingThousandsFormat && this.question.answers.length && !this.question.parentId ) {
      this.numWithThousandsFormat = this.thousandsFormat(this.question.answers[0].answer)
    }
    if (this.question.dataType === 'Float') {
      this.decimalPlace = "any"
    }
  }

  onBlur() {
    this.isFocused = false;
    if (this.myFormControl.value) {
      //use Number() to trim leading 0s
      const removeThousandsFormat = Number(this.myFormControl.value.toString().replace(/,/g, ''));
      this.numWithThousandsFormat = this.thousandsFormat(removeThousandsFormat)
      this.myFormControl.setValue(removeThousandsFormat.toString())
    }
    
    this.blur.emit();
  }

  onFocus() {
    this.isFocused = true;
    // thousands format should not be applied when the value is in focus/selected
    this.numWithThousandsFormat = this.myFormControl.value.toString().replace(/,/g, '')
  }

  // prevent decimals
  onlyNumbers() {
    const val = String(this.myFormControl.value);
    if (/[.]/g.test(val)) this.myFormControl.setValue(val.substring(0, val.indexOf('.')));
  }

  //when using thousands format, prevent alphabet input
  preventAlpha() {
    const val = String(this.myFormControl.value);
    if (/[^0-9\,.-]/.test(val)) {
      const match = val.match(/[^0-9\,.-]/);
      this.myFormControl.setValue(val.substring(0, val.indexOf(match[0])));
    }
    return true
  }

  thousandsFormat(initNum) {
    //split integer place and decimal place
    var integerParts = initNum.toString().split(".");
    //transfer integer place to thousands format
    integerParts[0] = integerParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    return integerParts.join(".")
  }
}

const numberFieldValidators = (question: IFormField) => {
  const validators = [];
  if (question.mandatory) validators.push(Validators.required);
  if (question.min) validators.push(Validators.min(question.min));
  if (question.max) validators.push(Validators.max(question.max));
  if (question.minLength) validators.push(Validators.minLength(question.minLength));
  if (question.maxLength) validators.push(Validators.maxLength(question.maxLength));

  // number inputs do not support minlength attribute so convert it into min chars
  if (question.minLength) {
    let minNumber = "1";
    for (let i = 0; i < question.minLength - 1; i++) {
      minNumber += "0";
    }
    validators.push(Validators.min(Number(minNumber)));
    question.min = Number(minNumber);
  }

  // number inputs doe not support maxlength attribute so convert it into max chars
  if (question.maxLength) {
    let maxNumber = "";

    for (let i = 0; i < question.maxLength; i++) {
      maxNumber += "9";
    }
    // only push validator if maxLength is less than max.
    // e.g max of 333 is valid if maxLength is 3.
    if (question.max) {
      if (Number(maxNumber) < question.max) validators.push(Validators.max(Number(maxNumber)));
    } else {
      validators.push(Validators.max(Number(maxNumber)));
    }
  }
  return validators;
};

export const makeNumberFieldFormControl = (q: IFormField, answer?: IAnswer) => {
  const formControlValue = answer ? answer.answer : (q.default != null ? Number(q.default) : "");
  q.validators = numberFieldValidators(q);
  const formControl = new UntypedFormControl(formControlValue, {validators: Validators.compose(q.validators)});
  if (!answer && typeof formControlValue === 'number') {
    formControl.markAsDirty();
  }
  return formControl;
};
